import React, { useState, useEffect } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { defaultWritingSettings } from '../contexts/SettingsContext';
import { API_BASE } from '../config';
import { useToast } from '../contexts/ToastContext';
import { Plus, AlignLeft, AlignCenter, AlignRight, AlignJustify, Minus } from 'lucide-react';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from './ui/tooltip';
import { InfoHint } from './InfoHint';
import { ClosePanelButton } from './ClosePanelButton';
import {
  COLOR_GRAY_100,
  COLOR_GRAY_400,
  COLOR_GRAY_700,
  COLOR_DANGER,
  COLOR_INFO,
  COLOR_WHITE,
} from '../colors';

interface WritingSettings {
  replaceHyphensWithDashes: boolean;
  collapseConsecutiveSpaces: boolean;
  enableLists: boolean;
  enableMentions: boolean;
  enableScriptMode: boolean;
  automaticReplacements: [string, string][];
  textJustification: 'left' | 'center' | 'right' | 'justified' | 'none';
}

interface SourceInfo {
  replaceHyphensWithDashes?: 'project' | 'document' | 'user' | 'default';
  collapseConsecutiveSpaces?: 'project' | 'document' | 'user' | 'default';
  enableLists?: 'project' | 'document' | 'user' | 'default';
  enableMentions?: 'project' | 'document' | 'user' | 'default';
  enableScriptMode?: 'project' | 'document' | 'user' | 'default';
  automaticReplacements?: 'project' | 'document' | 'user' | 'default';
  textJustification?: 'project' | 'document' | 'user' | 'default';
}

interface WritingSettingsProps {
  projectId?: string | null;
  documentId?: string | null;
  onUpdate?: () => void;
  onClose?: () => void;
  hideHeading?: boolean;
}

export const WritingSettings: React.FC<WritingSettingsProps> = ({ projectId, documentId, onUpdate, onClose, hideHeading = false }) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const { showToast, showErrorToast } = useToast();
  const [writingSettings, setWritingSettings] = useState<WritingSettings>(defaultWritingSettings);
  const [source, setSource] = useState<SourceInfo>({});
  const [documentWritingSettings, setDocumentWritingSettings] = useState<WritingSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [userReplacements, setUserReplacements] = useState<[string, string][]>([]);
  const [projectReplacements, setProjectReplacements] = useState<[string, string][]>([]);
  const [documentReplacements, setDocumentReplacements] = useState<[string, string][]>([]);
  const lastSaveToastAtRef = React.useRef<number>(0);

  useEffect(() => {
    const fetchSettings = async (): Promise<void> => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        let url = `${API_BASE}/api/settings/writing`;
        if (documentId && documentId !== 'new') {
          url = `${API_BASE}/api/documents/${documentId}/writing-settings`;
        } else if (projectId && projectId !== 'new') {
          url = `${API_BASE}/api/projects/${projectId}/writing-settings`;
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setWritingSettings(data.writingSettings || defaultWritingSettings);
          if (data.source) {
            setSource(data.source);
          }
          if (data.documentWritingSettings !== undefined) {
            setDocumentWritingSettings(data.documentWritingSettings || null);
          }
          if (data.userReplacements) {
            setUserReplacements(data.userReplacements);
          }
          if (data.projectReplacements) {
            setProjectReplacements(data.projectReplacements);
          }
          if (data.documentReplacements) {
            setDocumentReplacements(data.documentReplacements);
          }
        }
      } catch (error) {
        console.error('Error fetching writing settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();

    if (documentId && documentId !== 'new') {
      const handleDocumentSettingsUpdate = (event: Event): void => {
        const customEvent = event as CustomEvent<{ documentId: string }>;
        if (customEvent.detail.documentId === documentId) {
          fetchSettings();
        }
      };

      const handleProjectSettingsUpdate = (event: Event): void => {
        const customEvent = event as CustomEvent<{ projectId: string }>;
        if (projectId && customEvent.detail.projectId === projectId) {
          fetchSettings();
        }
      };

      const handleUserSettingsUpdate = (): void => {
        fetchSettings();
      };

      window.addEventListener('documentWritingSettingsUpdated', handleDocumentSettingsUpdate);
      window.addEventListener('projectWritingSettingsUpdated', handleProjectSettingsUpdate);
      window.addEventListener('userWritingSettingsUpdated', handleUserSettingsUpdate);

      return () => {
        window.removeEventListener('documentWritingSettingsUpdated', handleDocumentSettingsUpdate);
        window.removeEventListener('projectWritingSettingsUpdated', handleProjectSettingsUpdate);
        window.removeEventListener('userWritingSettingsUpdated', handleUserSettingsUpdate);
      };
    }

    return undefined;
  }, [token, projectId, documentId]);

  const updateSetting = async <K extends keyof WritingSettings>(
    key: K,
    value: WritingSettings[K]
  ): Promise<void> => {
    if (!token) {
      return;
    }

    const newSettings = { ...writingSettings, [key]: value };
    setWritingSettings(newSettings);

    try {
      let url = `${API_BASE}/api/settings/writing`;
      if (documentId && documentId !== 'new') {
        url = `${API_BASE}/api/documents/${documentId}/writing-settings`;
      } else if (projectId && projectId !== 'new') {
        url = `${API_BASE}/api/projects/${projectId}/writing-settings`;
      }

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ [key]: value }),
      });

      if (response.ok) {
        const data = await response.json();
        setWritingSettings(data.writingSettings);
        if (data.source) {
          setSource(data.source);
        }
        if (data.documentWritingSettings !== undefined) {
          setDocumentWritingSettings(data.documentWritingSettings || null);
        }
        if (data.userReplacements) {
          setUserReplacements(data.userReplacements);
        }
        if (data.projectReplacements) {
          setProjectReplacements(data.projectReplacements);
        }
        if (data.documentReplacements) {
          setDocumentReplacements(data.documentReplacements);
        }
        if (onUpdate) {
          onUpdate();
        }
        if (documentId && documentId !== 'new') {
          window.dispatchEvent(new CustomEvent('documentWritingSettingsUpdated', {
            detail: { documentId }
          }));
        } else if (projectId && projectId !== 'new') {
          window.dispatchEvent(new CustomEvent('projectWritingSettingsUpdated', {
            detail: { projectId }
          }));
        }
        if ((documentId && documentId !== 'new') || (projectId && projectId !== 'new')) {
          const now = Date.now();
          if (now - lastSaveToastAtRef.current > 1500) {
            lastSaveToastAtRef.current = now;
            showToast(t('settings_saved') || 'Settings saved successfully');
          }
        }
      } else {
        setWritingSettings(writingSettings);
        if ((documentId && documentId !== 'new') || (projectId && projectId !== 'new')) {
          showErrorToast(t('settings_save_error') || 'Failed to save settings');
        }
      }
    } catch (error) {
      console.error('Error updating writing settings:', error);
      setWritingSettings(writingSettings);
      if ((documentId && documentId !== 'new') || (projectId && projectId !== 'new')) {
        showErrorToast(t('settings_save_error') || 'Failed to save settings');
      }
    }
  };

  const handleCheckboxChange = async <K extends keyof WritingSettings>(
    key: K,
    currentValue: boolean
  ): Promise<void> => {
    await updateSetting(key, !currentValue as WritingSettings[K]);
  };

  const getCurrentLevelReplacements = (): [string, string][] => {
    if (documentId && documentId !== 'new') {
      return documentReplacements;
    } else if (projectId && projectId !== 'new') {
      return projectReplacements;
    }
    return userReplacements;
  };

  const handleAddReplacement = (): void => {
    const currentLevelReplacements = getCurrentLevelReplacements();
    const newReplacements: [string, string][] = [...currentLevelReplacements, ['', '']];
    if (documentId && documentId !== 'new') {
      setDocumentReplacements(newReplacements);
    } else if (projectId && projectId !== 'new') {
      setProjectReplacements(newReplacements);
    } else {
      setUserReplacements(newReplacements);
    }
  };

  const handleUpdateReplacement = async (level: 'user' | 'project' | 'document', index: number, from: string, to: string): Promise<void> => {
    let currentLevelReplacements: [string, string][];
    if (level === 'document') {
      currentLevelReplacements = documentReplacements;
    } else if (level === 'project') {
      currentLevelReplacements = projectReplacements;
    } else {
      currentLevelReplacements = userReplacements;
    }

    const newReplacements: [string, string][] = [...currentLevelReplacements];
    const previousReplacement = currentLevelReplacements[index] || ['', ''];
    const wasNewRow = previousReplacement[0] === '' && previousReplacement[1] === '';
    const bothFieldsSet = from.trim() !== '' && to.trim() !== '';

    newReplacements[index] = [from, to];
    
    if (level === 'document') {
      setDocumentReplacements(newReplacements);
    } else if (level === 'project') {
      setProjectReplacements(newReplacements);
    } else {
      setUserReplacements(newReplacements);
    }

    if (wasNewRow && !bothFieldsSet) {
      return;
    }

    await updateSetting('automaticReplacements', newReplacements);
  };

  const handleRemoveReplacement = async (level: 'user' | 'project' | 'document', index: number): Promise<void> => {
    let currentLevelReplacements: [string, string][];
    if (level === 'document') {
      currentLevelReplacements = documentReplacements;
    } else if (level === 'project') {
      currentLevelReplacements = projectReplacements;
    } else {
      currentLevelReplacements = userReplacements;
    }

    const newReplacements: [string, string][] = currentLevelReplacements.filter((_, i) => i !== index);
    if (level === 'document') {
      setDocumentReplacements(newReplacements);
    } else if (level === 'project') {
      setProjectReplacements(newReplacements);
    } else {
      setUserReplacements(newReplacements);
    }
    await updateSetting('automaticReplacements', newReplacements);
  };

  if (loading) {
    return <div>{t('loading')}</div>;
  }

  const getSourceTooltip = (key: keyof WritingSettings): string => {
    const sourceType = source[key];
    if (documentId && documentId !== 'new') {
      if (sourceType === 'document') {
        return '';
      }
      if (sourceType === 'project') {
        return t('set_at_project_level_click_to_set_document') || 'This setting is inherited from project settings. Click to set it at document level.';
      }
      if (sourceType === 'user') {
        return t('set_at_user_level_click_to_set_document') || 'This setting is inherited from your user settings. Click to set it at document level.';
      }
      return t('set_at_default_level_click_to_set_document') || 'This setting is using the default value. Click to set it at document level.';
    } else if (projectId && projectId !== 'new') {
      if (sourceType === 'project') {
        return '';
      }
      if (sourceType === 'user') {
        return t('set_at_user_level_click_to_set_project') || 'This setting is inherited from your user settings. Click to set it at project level.';
      }
      return t('set_at_default_level_click_to_set_project') || 'This setting is using the default value. Click to set it at project level.';
    }
    return '';
  };

  const isInherited = (key: keyof WritingSettings): boolean => {
    const sourceType = source[key];
    if (documentId && documentId !== 'new') {
      return sourceType !== 'document' && sourceType !== undefined;
    } else if (projectId && projectId !== 'new') {
      return sourceType !== 'project' && sourceType !== undefined;
    }
    return false;
  };

  const renderCheckbox = (
    key: 'replaceHyphensWithDashes' | 'collapseConsecutiveSpaces' | 'enableLists' | 'enableMentions' | 'enableScriptMode',
    label: string,
    helpText: string,
    infoHint?: { label: string; content: React.ReactNode },
  ) => {
    const inherited = isInherited(key);
    const tooltipText = getSourceTooltip(key);
    const checkbox = (
      <input
        type="checkbox"
        checked={writingSettings[key]}
        onChange={() => handleCheckboxChange(key, writingSettings[key])}
        style={{
          width: '1.25rem',
          height: '1.25rem',
          cursor: 'pointer',
          opacity: inherited ? 0.6 : 1,
        }}
      />
    );

    return (
      <div key={key}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            cursor: 'pointer',
          }}
        >
          {tooltipText ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {checkbox}
              </TooltipTrigger>
              <TooltipContent>
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            checkbox
          )}
          <span style={{ fontSize: '0.875rem', fontWeight: '500', opacity: inherited ? 0.6 : 1 }}>
            {label}
          </span>
        </label>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.375rem',
            marginLeft: '2rem',
          }}
        >
          <p style={{ fontSize: '0.75rem', color: COLOR_GRAY_400, margin: 0 }}>
            {helpText}
          </p>
          {infoHint && (
            <InfoHint
              label={infoHint.label}
              side="top"
              align="start"
              iconClassName="h-3.5 w-3.5"
              contentClassName="max-w-sm"
            >
              {infoHint.content}
            </InfoHint>
          )}
        </div>
      </div>
    );
  };

  const getHeaderTitle = (): string => {
    return t('document_settings');
  };

  const renderReplacementRow = (
    replacement: [string, string],
    index: number,
    isReadOnly: boolean,
    level: 'user' | 'project' | 'document',
    levelLabel?: string
  ): React.ReactNode => {
    const replaceEmpty = replacement[0].trim() === '';
    const withEmpty = replacement[1].trim() === '';

    return (
      <div key={`${level}-${index}`} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
        {levelLabel && (
          <span style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', minWidth: '60px', flexShrink: 0 }}>
            {levelLabel}
          </span>
        )}
        <input
          type="text"
          value={replacement[0]}
          onChange={(e) => !isReadOnly && handleUpdateReplacement(level, index, e.target.value, replacement[1])}
          placeholder={t('replace') || 'Replace'}
          disabled={isReadOnly}
          style={{
            flex: '1 1 0',
            minWidth: 0,
            padding: '0.5rem',
            border: replaceEmpty ? `1px solid ${COLOR_DANGER}` : '1px solid var(--border)',
            borderRadius: '6px',
            fontSize: '0.875rem',
            opacity: isReadOnly ? 0.6 : 1,
            backgroundColor: isReadOnly ? 'var(--muted)' : 'var(--background)',
            color: 'var(--foreground)',
            cursor: isReadOnly ? 'not-allowed' : 'text',
            fontVariantLigatures: 'none',
          }}
        />
        <span style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)', flexShrink: 0 }}>→</span>
        <input
          type="text"
          value={replacement[1]}
          onChange={(e) => !isReadOnly && handleUpdateReplacement(level, index, replacement[0], e.target.value)}
          placeholder={t('with') || 'With'}
          disabled={isReadOnly}
          style={{
            flex: '1 1 0',
            minWidth: 0,
            padding: '0.5rem',
            border: withEmpty ? `1px solid ${COLOR_DANGER}` : '1px solid var(--border)',
            borderRadius: '6px',
            fontSize: '0.875rem',
            opacity: isReadOnly ? 0.6 : 1,
            backgroundColor: isReadOnly ? 'var(--muted)' : 'var(--background)',
            color: 'var(--foreground)',
            cursor: isReadOnly ? 'not-allowed' : 'text',
            fontVariantLigatures: 'none',
          }}
        />
        {!isReadOnly && (
          <button
            type="button"
            className="group"
            onClick={() => handleRemoveReplacement(level, index)}
            style={{
              flexShrink: 0,
              padding: '0.5rem',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              color: COLOR_DANGER,
              display: 'flex',
              alignItems: 'center',
            }}
            aria-label={t('remove') || 'Remove'}
          >
            <RotatingDeleteIcon />
          </button>
        )}
      </div>
    );
  };

  const renderTextJustification = (): React.ReactNode => {
    const inherited = isInherited('textJustification');
    const tooltipText = getSourceTooltip('textJustification');
    const justificationOptions: Array<{ value: 'left' | 'center' | 'right' | 'justified' | 'none'; icon: React.ReactNode; label: string }> = [
      { value: 'left', icon: <AlignLeft className="h-4 w-4" />, label: t('text_justification_left') || 'Left' },
      { value: 'center', icon: <AlignCenter className="h-4 w-4" />, label: t('text_justification_center') || 'Center' },
      { value: 'right', icon: <AlignRight className="h-4 w-4" />, label: t('text_justification_right') || 'Right' },
      { value: 'justified', icon: <AlignJustify className="h-4 w-4" />, label: t('text_justification_justified') || 'Justified' },
      { value: 'none', icon: <Minus className="h-4 w-4" />, label: t('text_justification_none') || 'None' },
    ];

    const container = (
      <div>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.875rem', fontWeight: '500', opacity: inherited ? 0.6 : 1 }}>
            {t('text_justification') || 'Text justification'}
          </span>
        </div>
        <p style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', marginBottom: '0.75rem' }}>
          {t('text_justification_help') || 'Set the text alignment for the editor content.'}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginLeft: '0.5rem' }}>
          {justificationOptions.map((option) => {
            const isSelected = writingSettings.textJustification === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => updateSetting('textJustification', option.value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  backgroundColor: isSelected ? 'var(--muted)' : 'var(--background)',
                  cursor: 'pointer',
                  opacity: inherited ? 0.6 : 1,
                  transition: 'all 0.2s',
                  fontSize: '0.875rem',
                  color: isSelected ? 'var(--foreground)' : 'var(--muted-foreground)',
                }}
                onMouseEnter={(e) => {
                  if (!inherited) {
                    e.currentTarget.style.backgroundColor = 'var(--muted)';
                    e.currentTarget.style.borderColor = 'var(--border)';
                  }
                }}
                onMouseLeave={(e) => {
                  const selected = writingSettings.textJustification === option.value;
                  e.currentTarget.style.backgroundColor = selected ? 'var(--muted)' : 'var(--background)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}
              >
                {option.icon}
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );

    return tooltipText ? (
      <Tooltip>
        <TooltipTrigger asChild>
          {container}
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    ) : (
      container
    );
  };

  const renderAutomaticReplacements = (): React.ReactNode => {
    const currentLevelReplacements = getCurrentLevelReplacements();
    const hasInheritedReplacements = (documentId && documentId !== 'new' && (userReplacements.length > 0 || projectReplacements.length > 0)) ||
      (projectId && projectId !== 'new' && userReplacements.length > 0);

    return (
      <div>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
            {t('automatic_replacements') || 'Automatic replacements'}
          </span>
        </div>
        <p style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', marginBottom: '0.75rem' }}>
          {t('automatic_replacements_help') || 'Automatically replace text as you type. The first text will be replaced with the second.'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginLeft: '0.5rem', minWidth: 0 }}>
          {documentId && documentId !== 'new' && (
            <>
              {userReplacements.map((replacement, index) =>
                renderReplacementRow(replacement, index, true, 'user', 'User')
              )}
              {projectReplacements.map((replacement, index) =>
                renderReplacementRow(replacement, index, true, 'project', 'Project')
              )}
              {currentLevelReplacements.map((replacement, index) =>
                renderReplacementRow(replacement, index, false, 'document', 'Document')
              )}
            </>
          )}
          {projectId && projectId !== 'new' && !documentId && (
            <>
              {userReplacements.map((replacement, index) =>
                renderReplacementRow(replacement, index, true, 'user', 'User')
              )}
              {currentLevelReplacements.map((replacement, index) =>
                renderReplacementRow(replacement, index, false, 'project', 'Project')
              )}
            </>
          )}
          {!projectId && !documentId && (
            <>
              {currentLevelReplacements.map((replacement, index) =>
                renderReplacementRow(replacement, index, false, 'user')
              )}
            </>
          )}
          <button
            type="button"
            onClick={handleAddReplacement}
            style={{
              padding: '0.5rem',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--background)',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: 'var(--foreground)',
            }}
          >
            <Plus className="h-4 w-4" />
            {t('add_replacement') || 'Add replacement'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div style={{ padding: '1rem' }}>
        {hideHeading ? null : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>{getHeaderTitle()}</h2>
            {onClose && (
              <ClosePanelButton onClick={onClose} ariaLabel={t('close')} />
            )}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {renderCheckbox(
            'replaceHyphensWithDashes',
            t('replace_hyphens_with_dashes'),
            t('replace_hyphens_with_dashes_help')
          )}
          {renderCheckbox(
            'collapseConsecutiveSpaces',
            t('collapse_consecutive_spaces'),
            t('collapse_consecutive_spaces_help')
          )}
          {renderCheckbox(
            'enableLists',
            t('enable_lists'),
            t('enable_lists_help')
          )}
          {renderCheckbox(
            'enableMentions',
            t('enable_mentions'),
            t('enable_mentions_help')
          )}
          {renderCheckbox(
            'enableScriptMode',
            t('enable_script_mode'),
            t('enable_script_mode_help'),
            {
              label: t('enable_script_mode_more_info'),
              content: (
                <div className="space-y-2">
                  <p>{t('enable_script_mode_tooltip_intro')}</p>
                  <p>{t('enable_script_mode_tooltip_trigger')}</p>
                  <p>{t('enable_script_mode_tooltip_formatting')}</p>
                  <p>{t('enable_script_mode_tooltip_exit')}</p>
                </div>
              ),
            },
          )}
          {renderTextJustification()}
          {renderAutomaticReplacements()}
        </div>
      </div>
    </TooltipProvider>
  );
};

